// A giant wordmark that fills up with moving water, with bubbles rising inside.
const VIEW_W = 1000;
const VIEW_H = 220;
const LEVEL = 78; // final water surface (y in viewBox units; lower = fuller)

// Seamless sine-like wave: `period` wide crests from x=0 to `width`, closed down to the bottom.
function wavePath(amplitude, period, width, height) {
  let d = `M0 0 Q${period / 4} ${-amplitude} ${period / 2} 0`;
  for (let x = period; x <= width; x += period / 2) d += ` T${x} 0`;
  return `${d} V${height} H0 Z`;
}

const BACK_WAVE = wavePath(10, 260, VIEW_W + 520, 400);
const FRONT_WAVE = wavePath(8, 200, VIEW_W + 400, 400);

const bubbles = [
  { x: 90, r: 5, dur: 5.5, delay: 0 },
  { x: 230, r: 3, dur: 4.2, delay: 1.4 },
  { x: 380, r: 6, dur: 6.4, delay: 0.6 },
  { x: 520, r: 4, dur: 4.8, delay: 2.2 },
  { x: 640, r: 3, dur: 3.9, delay: 0.3 },
  { x: 760, r: 5, dur: 5.8, delay: 1.9 },
  { x: 900, r: 4, dur: 4.6, delay: 0.9 },
];

const textProps = {
  x: VIEW_W / 2,
  y: 192,
  textAnchor: 'middle',
  fontFamily: '"Space Grotesk", Inter, sans-serif',
  fontWeight: 700,
  fontSize: 228,
  textLength: VIEW_W - 16,
  lengthAdjust: 'spacingAndGlyphs',
};

export default function LiquidWord({ text, className = '' }) {
  const animate = !window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

  return (
    <svg viewBox={`0 0 ${VIEW_W} ${VIEW_H}`} className={className} aria-hidden="true">
      <defs>
        <clipPath id="liquid-word-clip">
          <text {...textProps}>{text}</text>
        </clipPath>
        <linearGradient id="liquid-word-fill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#f06d71" />
          <stop offset="45%" stopColor="#d02129" />
          <stop offset="100%" stopColor="#781b20" />
        </linearGradient>
      </defs>

      {/* empty glass */}
      <text {...textProps} fill="rgba(255,255,255,0.04)" stroke="rgba(255,255,255,0.5)" strokeWidth="1.5">
        {text}
      </text>

      <g clipPath="url(#liquid-word-clip)">
        <g transform={`translate(0 ${LEVEL})`}>
          {animate && (
            <animateTransform
              attributeName="transform"
              type="translate"
              from={`0 ${VIEW_H + 20}`}
              to={`0 ${LEVEL}`}
              dur="2.6s"
              calcMode="spline"
              keySplines="0.2 0.7 0.2 1"
              keyTimes="0;1"
              fill="freeze"
            />
          )}
          <g>
            <path d={BACK_WAVE} fill="#90191f" />
            {animate && (
              <animateTransform attributeName="transform" type="translate" from="0 -6" to="-520 -6" dur="5s" repeatCount="indefinite" />
            )}
          </g>
          <g>
            <path d={FRONT_WAVE} fill="url(#liquid-word-fill)" />
            {animate && (
              <animateTransform attributeName="transform" type="translate" from="-400 4" to="0 4" dur="3.4s" repeatCount="indefinite" />
            )}
          </g>
        </g>

        {animate &&
          bubbles.map((b) => (
            <circle key={b.x} cx={b.x} cy={VIEW_H} r={b.r} fill="none" stroke="rgba(255,255,255,0.6)" strokeWidth="1.5">
              <animate attributeName="cy" from={VIEW_H + 10} to={LEVEL + 6} dur={`${b.dur}s`} begin={`${2 + b.delay}s`} repeatCount="indefinite" />
              <animate attributeName="opacity" values="0;1;1;0" dur={`${b.dur}s`} begin={`${2 + b.delay}s`} repeatCount="indefinite" />
            </circle>
          ))}
      </g>
    </svg>
  );
}
